/**
 * SupervisorApprovalStep — Step 7 of the waste tracking wizard.
 * Early completion warning, supervisor badge prompt, reason dropdown,
 * session metrics, weight breakdown, and yield metrics.
 * Extracted from MobileWasteTracking.tsx for maintainability.
 */
import React from 'react';
import { ArrowLeft, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { formatYield } from '../../../hooks/useYieldFactor';
import { calculateTotalReturns } from '@tnb/yield-formula';
import type { SessionInfo } from '../../../types';
import type { ScannedBox } from '../wasteTrackingTypes';

interface SupervisorApprovalStepProps {
  session: SessionInfo;
  isEarlyCompletion: boolean;
  earlyCompletionReason: string;
  setEarlyCompletionReason: (v: string) => void;
  progressPct: number;
  elapsedTimeFormatted: string;
  boxCount: number;
  pace: number;
  initialMaterial: number;
  totalOutput: number;
  totalWaste: number;
  remainderIntact: number;
  remainderNotIntact: number;
  finishProductNotIntact: number;
  fpNotIntactFromLot: number;
  byProductTotal: number;
  yieldRate: number;
  expectedYield: number;
  hasYieldFactor: boolean;
  isFallbackYield: boolean;
  yieldOutsideTolerance: boolean;
  yieldThreshold: number | null;
  goBack: () => void;
}

const SupervisorApprovalStep: React.FC<SupervisorApprovalStepProps> = ({
  session,
  isEarlyCompletion,
  earlyCompletionReason,
  setEarlyCompletionReason,
  progressPct,
  elapsedTimeFormatted,
  boxCount,
  pace,
  initialMaterial,
  totalOutput,
  totalWaste,
  remainderIntact,
  remainderNotIntact,
  finishProductNotIntact,
  fpNotIntactFromLot,
  byProductTotal,
  yieldRate,
  expectedYield,
  hasYieldFactor,
  isFallbackYield,
  yieldOutsideTolerance,
  yieldThreshold,
  goBack,
}) => {
  const { t } = useLanguage();

  const totalReturns = calculateTotalReturns({
    remainderIntact,
    rawMaterialNotIntact: remainderNotIntact,
    byProductTotal,
    fpNotIntactNewGenerated: finishProductNotIntact,
  });
  // Carryover is finished product from a prior session's lot — it counts toward
  // Output but did not come from this session's Input. Add it to the input side
  // of the balance so: Input + Carryover = Waste + Returns + Output.
  const accountedWeight = totalOutput + totalWaste + totalReturns;
  const difference = Math.abs(initialMaterial + fpNotIntactFromLot - accountedWeight);
  const isBalanced = difference <= 0.1; // Allow 0.1 lb tolerance for rounding
  const hasCarryover = fpNotIntactFromLot > 0;

  // Only treat negative variance (under-yield) as a warning; over-yield is good.
  const isUnderYield = yieldRate < expectedYield;
  const showVarianceWarning = yieldOutsideTolerance && isUnderYield;

  return (
    <div className="flex-1 flex flex-col">
      {/* Early Completion Warning Banner - shown first for visibility */}
      {isEarlyCompletion && progressPct < 100 && (
        <div className="bg-amber-100 dark:bg-amber-900/30 border border-amber-400 dark:border-amber-600 rounded-xl p-3 mb-3">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-bold text-sm">{t('mobile.earlyCompletionWarning') || 'Early Session Completion'}</p>
              <p className="text-xs">{t('mobile.earlyCompletionWarningDesc') || `Progress: ${progressPct.toFixed(2)}% - Session will be marked as completed by override`}</p>
            </div>
          </div>
        </div>
      )}

      {/* Supervisor badge prompt - compact inline layout */}
      <div className={`flex items-center gap-3 p-3 rounded-xl border mb-3 ${
        isEarlyCompletion && !earlyCompletionReason
          ? 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 animate-pulse'
      }`}>
        <button
          onClick={goBack}
          className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('mobile.supervisorSignOff') || 'Supervisor Sign-Off Required'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {isEarlyCompletion && !earlyCompletionReason
              ? (t('mobile.selectReasonFirst') || 'Select a reason below')
              : (t('mobile.scanSupervisorBadgeToComplete') || 'Scan supervisor badge to complete')
            }
          </p>
        </div>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isEarlyCompletion && !earlyCompletionReason
            ? 'bg-gray-200 dark:bg-gray-700'
            : 'bg-blue-100 dark:bg-blue-800'
        }`}>
          <ShieldCheck className={`w-5 h-5 ${
            isEarlyCompletion && !earlyCompletionReason
              ? 'text-gray-400 dark:text-gray-500'
              : 'text-blue-600 dark:text-blue-400'
          }`} />
        </div>
      </div>

      {/* Early Completion Reason Dropdown - only shown for early completion */}
      {isEarlyCompletion && (
        <div className="mb-3 flex items-center justify-end gap-3">
          <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {t('mobile.earlyCompletionReason') || 'Reason'} <span className="text-red-500">*</span>
          </label>
          <select
            value={earlyCompletionReason}
            onChange={(e) => setEarlyCompletionReason(e.target.value)}
            className={`w-40 p-2 rounded-lg border text-sm ${
              earlyCompletionReason
                ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                : 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
            } dark:text-white`}
          >
            <option value="">{t('mobile.selectReason') || 'Select...'}</option>
            <option value="shift_ended">{t('mobile.reasonShiftEnded') || 'Shift Ended'}</option>
            <option value="material_shortage">{t('mobile.reasonMaterialShortage') || 'Material Shortage'}</option>
            <option value="equipment_issue">{t('mobile.reasonEquipmentIssue') || 'Equipment Issue'}</option>
            <option value="other">{t('mobile.reasonOther') || 'Other'}</option>
          </select>
        </div>
      )}

      {/* Session Summary - matching Supervisor Override modal layout */}
      <div className="space-y-3 mb-4">
        {/* Row 1: Key metrics - 3 columns */}
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-xl text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('mobile.elapsedTime') || 'Elapsed'}</p>
            <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{elapsedTimeFormatted}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-xl text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('mobile.boxCount') || 'Boxes'}</p>
            <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{boxCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-xl text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('mobile.pace') || 'Pace'}</p>
            <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{pace.toFixed(1)}/hr</p>
          </div>
        </div>

        {/* Row 2: Progress bar - full width */}
        <div className="bg-white dark:bg-gray-800 p-3 rounded-xl">
          <div className="flex justify-between items-center mb-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('mobile.progress') || 'Progress'}</p>
            <p className={`text-sm font-bold ${progressPct >= 100 ? 'text-green-600' : 'text-gray-900 dark:text-white'}`}>
              {progressPct.toFixed(2)}%
            </p>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full ${progressPct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${Math.min(progressPct, 100)}%` }}
            />
          </div>
        </div>

        {/* Row 3: Weight breakdown - Input = Waste + Returns + Output */}
        <div className={`p-3 rounded-xl border ${isBalanced
          ? 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600'
          : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'}`}>
          <div className="flex items-center justify-between text-sm md:text-base gap-1">
            <div className="text-center min-w-0 flex-1">
              <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{t('mobile.inputWeight') || 'Input'}</p>
              <p className="font-bold text-gray-900 dark:text-white text-sm">{initialMaterial.toFixed(2)}</p>
            </div>
            {hasCarryover && (
              <>
                <span className="text-gray-400 text-xs">+</span>
                <div className="text-center min-w-0 flex-1">
                  <p className="text-[10px] text-purple-500 dark:text-purple-400 truncate">{t('mobile.carryover') || 'Carryover'}</p>
                  <p className="font-bold text-sm text-purple-600 dark:text-purple-400">{fpNotIntactFromLot.toFixed(2)}</p>
                </div>
              </>
            )}
            <span className={`text-xs font-bold ${isBalanced ? 'text-gray-600 dark:text-gray-300' : 'text-amber-600 dark:text-amber-400'}`}>=</span>
            <div className="text-center min-w-0 flex-1">
              <p className="text-[10px] text-red-500 dark:text-red-400 truncate">{t('mobile.waste') || 'Waste'}</p>
              <p className="font-bold text-sm text-red-600 dark:text-red-400">{totalWaste.toFixed(2)}</p>
            </div>
            <span className="text-gray-400 text-xs">+</span>
            <div className="text-center min-w-0 flex-1">
              <p className="text-[10px] text-blue-500 dark:text-blue-400 truncate">{t('mobile.returns') || 'Returns'}</p>
              <p className="font-bold text-sm text-blue-600 dark:text-blue-400">{totalReturns.toFixed(2)}</p>
            </div>
            <span className="text-gray-400 text-xs">+</span>
            <div className="text-center min-w-0 flex-1">
              <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{t('mobile.outputWeight') || 'Output'}</p>
              <p className={`font-bold text-sm ${isBalanced ? 'text-gray-900 dark:text-white' : 'text-amber-600 dark:text-amber-400'}`}>{totalOutput.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Row 4: Yield metrics - 2 columns with status indicator */}
        <div className={`p-3 rounded-xl ${showVarianceWarning || !hasYieldFactor
          ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700'
          : 'bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700'}`}>
          {yieldOutsideTolerance && (
            <p className={`text-base font-bold text-center mb-2 flex items-center justify-center gap-2 ${
              showVarianceWarning ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'
            }`}>
              <AlertTriangle className="w-5 h-5" />
              {t('mobile.yieldVarianceDetected') || 'Yield Variance Detected'}
            </p>
          )}
          {!hasYieldFactor && (
            <p className="text-base font-bold text-amber-600 dark:text-amber-400 text-center mb-2 flex items-center justify-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {t('mobile.yieldReviewRequired') || 'Yield Review Required'}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('mobile.expectedYield') || 'Expected Yield'}</p>
              <p className={`text-base md:text-lg font-bold ${
                !hasYieldFactor
                  ? 'text-gray-400 dark:text-gray-500'
                  : isFallbackYield
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-gray-900 dark:text-white'
              }`}>
                {hasYieldFactor ? formatYield(expectedYield, undefined, session?.raw_material_yield_source) : '?'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('mobile.actualYield') || 'Actual Yield'}</p>
              <p className={`text-base md:text-lg font-bold ${showVarianceWarning ? 'text-red-600 dark:text-red-400' : !hasYieldFactor ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                {formatYield(yieldRate)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupervisorApprovalStep;
